/*
   parts-finder.js
   ------------------------------------------------------------------
   Opens and closes the Parts Finder sheet (snippets/parts-finder.liquid) the same
   way garage.js opens the Garage:

   - any <a href="...#parts-finder"> on the page opens it, including links inside
     Shopify's <shopify-account> sheet (composedPath sees through its shadow root);
     the account sheet is closed first so nothing is left open underneath.
   - arriving on, or moving to, a URL ending in #parts-finder opens it on load.
   - the --shopify-account-* tokens in effect on <shopify-account> are copied onto
     the dialog so fonts, colours and radii match the account sheet.
   - closing (close button with data-parts-finder-close, click outside, Esc) plays
     the same .closing exit animation.
   - the signed-out "Sign in" button (data-parts-finder-sign-in) closes the sheet and
     opens Shopify's account sheet, falling back to the login page.
   - a link to #garage inside the sheet (the "Manage garage" button) closes this
     sheet; garage.js then opens the Garage as it does for any #garage link.
   - the header's mobile garage-finder bar is one of those links.

   Sections 1-3 are a copy of garage.js with the id and hooks renamed, so the two
   behave identically. The finder itself (year / make / model) is driven by
   garage-finder.js through the <garage-finder> element in the snippet.
*/
(function () {
  var HASH = '#parts-finder';
  var SHEET_ID = 'parts-finder';
  var CLOSE_WAIT = 400; // ms to wait for the exit animation / Shopify's close event before moving on
  var ACCOUNT_TOKENS = [
    'dialog-position-top', 'font-heading', 'font-heading-weight', 'font-heading-transform',
    'font-body', 'font-body-weight', 'font-body-transform', 'radius-base', 'radius-dialog',
    'radius-button', 'radius-button-small', 'radius-input', 'color-background', 'color-text',
    'color-accent', 'color-accent-text', 'color-accent-hover', 'color-border',
    'color-background-subdued', 'color-text-subdued', 'color-shadow',
    'color-control-background', 'color-control-text', 'color-card-background', 'color-card-text'
  ];

  function getAccountHosts() {
    return Array.prototype.slice.call(document.querySelectorAll('shopify-account'));
  }

  function getVisibleAccountHost() {
    var hosts = getAccountHosts();
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].getClientRects().length) return hosts[i];
    }
    return hosts[0] || null;
  }

  function getSheet() {
    return document.getElementById(SHEET_ID);
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function openSheet() {
    var sheet = getSheet();
    if (!sheet || sheet.open) return;

    var host = getVisibleAccountHost();
    if (host) {
      var hostStyles = getComputedStyle(host);
      ACCOUNT_TOKENS.forEach(function (name) {
        var value = hostStyles.getPropertyValue('--shopify-account-' + name).trim();
        if (value) sheet.style.setProperty('--shopify-account-' + name, value);
      });
    }

    sheet.classList.remove('closing');
    sheet.showModal();
  }

  // Same exit animation Shopify uses: add .closing, close when it finishes.
  function closeSheet(sheet, then) {
    if (!sheet.open || sheet.classList.contains('closing')) return;
    if (reducedMotion()) { sheet.close(); if (then) then(); return; }
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      sheet.removeEventListener('animationend', onEnd);
      sheet.classList.remove('closing');
      sheet.close();
      if (then) then();
    }
    function onEnd(event) {
      if (event.target === sheet) finish();
    }
    sheet.addEventListener('animationend', onEnd);
    setTimeout(finish, CLOSE_WAIT);
    sheet.classList.add('closing');
  }

  // Close every open Shopify account sheet through its own close button, then run `then`.
  function closeAccountSheet(then) {
    var open = [];
    getAccountHosts().forEach(function (host) {
      var root = host.shadowRoot;
      var dialog = root && root.querySelector('dialog');
      if (dialog && dialog.open) {
        open.push({ host: host, sheet: dialog, button: root.querySelector('#close-button') });
      }
    });
    if (!open.length) { then(); return; }

    var done = false;
    function go() {
      if (done) return;
      done = true;
      open.forEach(function (item) {
        item.host.removeEventListener('close', onClose);
        if (item.sheet.open) item.sheet.close();
      });
      then();
    }
    function onClose() {
      var stillOpen = open.some(function (item) { return item.sheet.open; });
      if (!stillOpen) go();
    }

    open.forEach(function (item) {
      item.host.addEventListener('close', onClose);
      if (item.button) item.button.click();
    });
    setTimeout(go, CLOSE_WAIT);
  }

  // Also close the Garage if it is open, so the Parts Finder isn't stacked on top of it.
  function closeGarageSheet(then) {
    var garage = document.getElementById('garage');
    if (!garage || !garage.open) { then(); return; }
    var closer = garage.querySelector('[data-garage-close]');
    if (closer) closer.click(); else garage.close();
    setTimeout(then, reducedMotion() ? 0 : CLOSE_WAIT);
  }

  // 1. Catch clicks on any link ending in #parts-finder.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!getSheet()) return; // no sheet on this page: let the link navigate

    var path = event.composedPath ? event.composedPath() : [];
    var link = null;
    for (var i = 0; i < path.length; i++) {
      if (path[i].tagName === 'A' && path[i].hash === HASH) { link = path[i]; break; }
    }
    if (!link) return;

    event.preventDefault();
    closeAccountSheet(function () { closeGarageSheet(openSheet); });
  }, true);

  // 2. Fallback: arrived on, or moved to, a URL ending in #parts-finder.
  function openFromHash() {
    if (location.hash !== HASH || !getSheet()) return;
    history.replaceState(null, '', location.pathname + location.search);
    openSheet();
  }
  function openFromHashOnLoad() {
    if (location.hash !== HASH || !getSheet()) return;
    openFromHash();
    window.scrollTo(0, 0);
    window.addEventListener('load', function () { window.scrollTo(0, 0); }, { once: true });
  }
  window.addEventListener('hashchange', openFromHash);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFromHashOnLoad);
  } else {
    openFromHashOnLoad();
  }

  // 3. Closing: close button, click outside, Esc, or a #garage link inside the sheet
  //    (garage.js catches that same click in the capture phase and opens the Garage).
  // Shopify's own account button (the header avatar), inside the component.
  function getAccountButton() {
    var host = getVisibleAccountHost();
    var root = host && host.shadowRoot;
    return root ? root.querySelector('button.account-button, [part~="signed-out-avatar"]') : null;
  }

  document.addEventListener('click', function (event) {
    var sheet = getSheet();
    if (!sheet || !sheet.open) return;

    // Signed-out "Sign in": close this sheet, then open Shopify's account sheet.
    // If the account button can't be reached, leave the link alone (login page).
    var signIn = event.target.closest && event.target.closest('[data-parts-finder-sign-in]');
    if (signIn && sheet.contains(signIn)) {
      var accountButton = getAccountButton();
      if (!accountButton) return;
      event.preventDefault();
      closeSheet(sheet, function () { accountButton.click(); });
      return;
    }

    var garageLink = event.target.closest && event.target.closest('a[href$="#garage"]');
    if (garageLink && sheet.contains(garageLink)) {
      if (!document.getElementById('garage')) return; // no Garage on this page: let it navigate
      closeSheet(sheet);
      return;
    }
    if (event.target === sheet) { closeSheet(sheet); return; }
    var closer = event.target.closest && event.target.closest('[data-parts-finder-close]');
    if (closer && sheet.contains(closer)) {
      event.preventDefault();
      closeSheet(sheet);
    }
  });

  document.addEventListener('cancel', function (event) {
    var sheet = getSheet();
    if (sheet && event.target === sheet) {
      event.preventDefault();
      closeSheet(sheet);
    }
  }, true);
})();
